import { Observable, pipe } from 'rxjs';
import { first, switchMap, withLatestFrom } from 'rxjs/operators';

import { Actions } from '~/actions';
import { TypedAction, ofType } from '@mode/legacy/util-domain';
import { AppStore } from '~/types/main.types';
import { ChartTypes } from '@mode/shared/contract-common';
import { fromChartSelectors } from '@mode/charts/domain';

export function getSnapshotFromAction<
  A extends Extract<U, { type: T1; chartToken: string }>,
  U extends Actions,
  T1 extends Actions['type'],
>(store: AppStore, type: T1) {
  return pipe(
    ofType<A>(type),
    switchMap(
      (action) => store.select(fromChartSelectors.createChartSnapshotSelector(action.chartToken)).pipe(first()),
      (action, snapshot) => ({ action, snapshot, token: action.chartToken })
    )
  );
}

export function withLatestSnapshot<A extends TypedAction<T1> & Record<P, string>, T1 extends string, P extends keyof A>(
  store: AppStore,
  tokenProp: P
) {
  return pipe<
    Observable<A>,
    Observable<{ action: A; token: string; snapshot: ChartTypes.ChartSnapshot; queryToken: string }>
  >(
    withLatestFrom(store.select(fromChartSelectors.selectChartState), (action, charts) => ({
      action,
      token: action[tokenProp],
      snapshot: {
        encoding: charts.encodings.get(action[tokenProp]),
        chartType: charts.chartTypes.get(action[tokenProp]),
        title: charts.titles.get(action[tokenProp]),
        description: charts.descriptions.get(action[tokenProp]),
        caption: charts.captions.get(action[tokenProp]),
        format: charts.formats.get(action[tokenProp]),
        fieldFormats: charts.fieldFormats.get(action[tokenProp]),
        fieldColors: charts.fieldColors.get(action[tokenProp]),
        colorPaletteToken: charts.colorPaletteTokens.get(action[tokenProp]),
        facetSizes: charts.facetSizes.get(action[tokenProp]),
        isAutoUpdateDisabled: charts.isAutoUpdateDisabled.get(action[tokenProp]) ?? false,
      },
      queryToken: charts.queryTokens.get(action[tokenProp]),
    }))
  );
}
